<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import otpLockoutsHttpService, { type OtpLockout } from '@/services/http/otpLockoutsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, locale } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const lockoutsQuery = useQuery({
  queryKey: computed(() => ['admin', 'otp-lockouts', locale.value]),
  queryFn: () => otpLockoutsHttpService.getLockouts(),
})

const lockouts = computed<OtpLockout[]>(() => lockoutsQuery.data.value ?? [])

function invalidate() {
  queryClient.invalidateQueries({ queryKey: ['admin', 'otp-lockouts'] })
}

function notify(success: boolean, detail: string) {
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? t('common.success') : t('common.error'),
    detail,
    life: 3000,
  })
}

function contactOf(lockout: OtpLockout): string {
  return lockout.email ?? lockout.phoneNumber ?? '—'
}

function channelLabel(lockout: OtpLockout): string {
  return lockout.channel === 'email' || lockout.channel === 'sms'
    ? t(`otpLockouts.channels.${lockout.channel}`)
    : lockout.channel
}

function formatLockedUntil(value: string): string {
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}.${pad(date.getMonth() + 1)}`
}

type TagSeverity = 'warn' | 'danger' | 'secondary'

function levelTag(lockout: OtpLockout): { value: string; severity: TagSeverity } {
  if (lockout.permanentlyLocked) {
    return { value: t('otpLockouts.permanentlyLocked'), severity: 'danger' }
  }
  if (lockout.lockedUntil != null) {
    return {
      value: t('otpLockouts.levelUntil', {
        level: lockout.lockoutLevel,
        until: formatLockedUntil(lockout.lockedUntil),
      }),
      severity: 'warn',
    }
  }
  // Временный блок истёк, но уровень сохранён — следующее превышение будет жёстче.
  return {
    value: t('otpLockouts.levelOnly', { level: lockout.lockoutLevel }),
    severity: 'secondary',
  }
}

// --- Разблокировка (confirm-диалог) ---

const showUnlockDialog = ref(false)
const selectedLockout = ref<OtpLockout | null>(null)

function openUnlockDialog(lockout: OtpLockout) {
  selectedLockout.value = lockout
  showUnlockDialog.value = true
}

const unlockMutation = useMutation({
  mutationFn: (id: string) => otpLockoutsHttpService.unlock(id),
  onSuccess: () => {
    notify(true, t('otpLockouts.toasts.unlocked'))
    showUnlockDialog.value = false
    invalidate()
  },
  onError: () => notify(false, t('otpLockouts.toasts.unlockFailed')),
})

function confirmUnlock() {
  if (selectedLockout.value == null) return
  unlockMutation.mutate(selectedLockout.value.id)
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex items-center justify-between w-full">
      <h1 class="text-xl font-bold">{{ t('otpLockouts.title') }}</h1>
    </div>

    <DataTable
      :value="lockouts"
      :loading="lockoutsQuery.isLoading.value"
      data-key="id"
      class="w-full"
      :pt="{ bodyRow: { 'data-test': 'lockout-row' } }"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('otpLockouts.empty') }}</div>
      </template>

      <Column :header="t('otpLockouts.columns.contact')">
        <template #body="{ data }">
          <span data-test="lockout-contact">{{ contactOf(data) }}</span>
        </template>
      </Column>
      <Column :header="t('otpLockouts.columns.channel')">
        <template #body="{ data }">
          {{ channelLabel(data) }}
        </template>
      </Column>
      <Column field="sendCount" :header="t('otpLockouts.columns.sendCount')" />
      <Column :header="t('otpLockouts.columns.level')">
        <template #body="{ data }">
          <Tag :value="levelTag(data).value" :severity="levelTag(data).severity" />
        </template>
      </Column>
      <Column :header="t('otpLockouts.columns.actions')">
        <template #body="{ data }">
          <Button
            size="small"
            :label="t('otpLockouts.unlock')"
            severity="warn"
            outlined
            data-test="lockout-unlock"
            @click="openUnlockDialog(data)"
          />
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="showUnlockDialog"
      :header="t('otpLockouts.confirm.title')"
      modal
      dismissable-mask
      class="w-[90vw] sm:w-[480px]"
    >
      <div class="flex flex-col gap-4">
        <p class="m-0">
          {{
            t('otpLockouts.confirm.message', {
              contact: selectedLockout ? contactOf(selectedLockout) : '',
            })
          }}
        </p>
        <div class="flex gap-2">
          <Button
            class="w-full"
            :label="t('common.cancel')"
            text
            @click="showUnlockDialog = false"
          />
          <Button
            class="w-full"
            :label="t('otpLockouts.unlock')"
            severity="warn"
            :loading="unlockMutation.isPending.value"
            data-test="lockout-unlock-confirm"
            @click="confirmUnlock"
          />
        </div>
      </div>
    </Dialog>
  </section>
</template>
